import React, { useEffect, useState } from 'react';
import { superApi, useSuperAdminAuth } from './SuperAdminAuth';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';

/**
 * Combined page: super-admin Roles + Staff management.
 * Tabs: [Staff, Roles]
 */
export const SuperStaffRolesPage = ({ defaultTab = 'staff' }) => {
  const { hasPerm } = useSuperAdminAuth();
  const [tab, setTab] = useState(defaultTab);
  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex items-center gap-4">
        <h1 className="text-2xl font-semibold text-white">
          {tab === 'staff' ? 'Super-Admin Staff' : 'Super-Admin Roles'}
        </h1>
      </div>
      <div className="flex gap-2 mb-6 border-b border-slate-800">
        {hasPerm('super.staff.view') && (
          <button
            onClick={() => setTab('staff')}
            className={`px-4 py-2 text-sm border-b-2 ${tab === 'staff' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-white'}`}
            data-testid="super-tab-staff"
          >Staff</button>
        )}
        {hasPerm('super.roles.view') && (
          <button
            onClick={() => setTab('roles')}
            className={`px-4 py-2 text-sm border-b-2 ${tab === 'roles' ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-white'}`}
            data-testid="super-tab-roles"
          >Roles</button>
        )}
      </div>
      {tab === 'staff' ? <StaffTab /> : <RolesTab />}
    </div>
  );
};

const StaffTab = () => {
  const { hasPerm, user: me } = useSuperAdminAuth();
  const canManage = hasPerm('super.staff.manage');
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', super_role_id: '', password: '' });
  const [tempPassword, setTempPassword] = useState(null);

  const load = async () => {
    try {
      const [staffR, rolesR] = await Promise.all([
        superApi.get('/super/staff'),
        superApi.get('/super/roles'),
      ]);
      setStaff(staffR.data.staff || []);
      setRoles(rolesR.data.roles || []);
    } catch (e) {
      toast.error('Failed to load staff');
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await superApi.post('/super/staff', form);
      if (res.data.temp_password) setTempPassword(res.data.temp_password);
      toast.success(res.data.promoted ? 'User promoted to super-admin' : 'Invite created');
      await load();
      if (!res.data.temp_password) {
        setShowInvite(false);
        setForm({ email: '', full_name: '', super_role_id: '', password: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const changeRole = async (staffId, roleId) => {
    try {
      await superApi.put(`/super/staff/${staffId}/role`, { super_role_id: roleId });
      toast.success('Role updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove super-admin access for ${s.email}?`)) return;
    try {
      await superApi.delete(`/super/staff/${s.id}`);
      toast.success('Super-admin access removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-400">{staff.length} staff</div>
        {canManage && (
          <button
            onClick={() => setShowInvite(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium"
            data-testid="super-invite-staff-btn"
          ><Plus className="w-4 h-4" /> Invite Staff</button>
        )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950 text-slate-400">
            <tr><th className="text-left p-3 font-medium">Email</th><th className="text-left p-3 font-medium">Name</th><th className="text-left p-3 font-medium">Role</th><th></th></tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-slate-800">
                <td className="p-3 text-white">{s.email}{s.id === me?.id && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">you</span>}</td>
                <td className="p-3 text-slate-300">{s.full_name || '—'}</td>
                <td className="p-3">
                  {canManage && s.id !== me?.id ? (
                    <select
                      value={s.super_role_id || ''}
                      onChange={(e) => changeRole(s.id, e.target.value)}
                      className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200"
                    >
                      {roles.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                    </select>
                  ) : (
                    <span className="text-amber-400">{s.super_role_name}</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {canManage && s.id !== me?.id && (
                    <button onClick={() => remove(s)} className="text-red-400 hover:text-red-300 p-1" title="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <Modal onClose={() => { setShowInvite(false); setTempPassword(null); }} title="Invite Super-Admin Staff">
          {tempPassword ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-300">Staff created. Share these credentials securely:</div>
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <div className="text-xs text-slate-500">Email</div>
                <div className="text-white font-mono text-sm">{form.email}</div>
                <div className="text-xs text-slate-500 mt-2">Temporary Password</div>
                <div className="text-amber-400 font-mono text-sm">{tempPassword}</div>
              </div>
              <button
                onClick={() => { setShowInvite(false); setTempPassword(null); setForm({ email: '', full_name: '', super_role_id: '', password: '' }); }}
                className="w-full py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black"
              >Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
              <Field label="Full Name"><input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} /></Field>
              <Field label="Role">
                <select required value={form.super_role_id} onChange={(e) => setForm({ ...form, super_role_id: e.target.value })} className={inputCls}>
                  <option value="">Select role…</option>
                  {roles.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                </select>
              </Field>
              <Field label="Password (leave empty to auto-generate)"><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} /></Field>
              <button type="submit" className="w-full py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium">Invite</button>
            </form>
          )}
        </Modal>
      )}
    </>
  );
};

const RolesTab = () => {
  const { hasPerm } = useSuperAdminAuth();
  const canManage = hasPerm('super.roles.manage');
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [rolesR, permsR] = await Promise.all([
        superApi.get('/super/roles'),
        superApi.get('/super/permissions'),
      ]);
      setRoles(rolesR.data.roles || []);
      setPerms(permsR.data.permissions || {});
    } catch (e) {
      toast.error('Failed to load roles');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (role) => {
    try {
      const payload = { name: role.name, description: role.description, permissions: role.permissions };
      if (role.id.startsWith('new_')) {
        await superApi.post('/super/roles', payload);
      } else {
        await superApi.put(`/super/roles/${role.id}`, payload);
      }
      toast.success('Role saved');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await superApi.delete(`/super/roles/${role.id}`);
      toast.success('Role deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Group perms by prefix (super.companies.*, super.billing.* etc.)
  const groupedPerms = React.useMemo(() => {
    const g = {};
    Object.entries(perms).forEach(([k, label]) => {
      const group = k.split('.')[1] || 'other';
      if (!g[group]) g[group] = [];
      g[group].push({ key: k, label });
    });
    return g;
  }, [perms]);

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-400">{roles.length} roles</div>
        {canManage && (
          <button
            onClick={() => setEditing({ id: `new_${Date.now()}`, name: '', description: '', permissions: [], is_built_in: false })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium"
            data-testid="super-new-role-btn"
          ><Plus className="w-4 h-4" /> New Role</button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-white font-medium">{r.name}</div>
                {r.is_built_in && <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">built-in</span>}
              </div>
              {canManage && !r.is_built_in && (
                <div className="flex gap-1">
                  <button onClick={() => setEditing(r)} className="p-1 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(r)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            {r.description && <div className="text-xs text-slate-400 mb-3">{r.description}</div>}
            <div className="text-xs text-slate-500">{(r.permissions || []).length} permissions</div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id.startsWith('new_') ? 'New Role' : `Edit ${editing.name}`} wide>
          <div className="space-y-3">
            <Field label="Name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} /></Field>
            <Field label="Description"><input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} /></Field>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {Object.entries(groupedPerms).map(([group, perms]) => (
                <div key={group} className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                  <div className="text-xs uppercase tracking-wider text-amber-400 mb-2">{group}</div>
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-start gap-2 py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editing.permissions.includes(p.key)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...editing.permissions, p.key]
                            : editing.permissions.filter((x) => x !== p.key);
                          setEditing({ ...editing, permissions: next });
                        }}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm text-slate-200">{p.label}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{p.key}</div>
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg border border-slate-800 text-slate-300 hover:bg-slate-800">Cancel</button>
              <button onClick={() => save(editing)} className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium inline-flex items-center justify-center gap-1"><Check className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// Small helpers -----------------------------------------------------------
const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white focus:outline-none focus:border-amber-500/50';
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className={`bg-slate-900 border border-slate-800 rounded-xl p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-md'}`} onClick={(e) => e.stopPropagation()}>
      <div className="text-lg font-semibold text-white mb-4">{title}</div>
      {children}
    </div>
  </div>
);

export default SuperStaffRolesPage;
